import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Dropdown, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import TicketService from '../../../services/TicketService';

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
  }, [filter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const response = await TicketService.getMyTickets(status);
      setTickets(response.data || []);
      setStats(response.stats || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'danger',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      urgent: 'danger'
    };
    return <Badge bg={variants[priority] || 'secondary'} className="text-uppercase">{priority}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      technical: 'Technical Support',
      billing: 'Billing',
      feature_request: 'Feature Request',
      general: 'General Inquiry',
      account: 'Account'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="ticket-list">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">My Support Tickets</h4>
          <Link to="/tickets/new" className="btn btn-primary btn-sm">
            <i className="flaticon-plus mr-2"></i>
            New Ticket
          </Link>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Stats */}
          {stats && (
            <div className="row mb-4">
              <div className="col-md-3 col-6 mb-2">
                <Card className="text-center bg-light">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.open || 0}</h3>
                    <small className="text-muted">Open</small>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <Card className="text-center bg-light">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.in_progress || 0}</h3>
                    <small className="text-muted">In Progress</small>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <Card className="text-center bg-light">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.resolved || 0}</h3>
                    <small className="text-muted">Resolved</small>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <Card className="text-center bg-light">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.total || 0}</h3>
                    <small className="text-muted">Total</small>
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="mb-3">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm" id="filter-dropdown">
                Filter: {filter === 'all' ? 'All Tickets' : filter.replace('_', ' ').toUpperCase()}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setFilter('all')}>All Tickets</Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter('open')}>Open</Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter('in_progress')}>In Progress</Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter('resolved')}>Resolved</Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter('closed')}>Closed</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Tickets Table */}
          {tickets.length === 0 ? (
            <div className="text-center py-5">
              <i className="flaticon-support" style={{ fontSize: '48px', color: '#ccc' }}></i>
              <p className="mt-3 text-muted">No tickets found. Create your first support ticket to get help.</p>
              <Link to="/tickets/new" className="btn btn-primary">
                <i className="flaticon-plus mr-2"></i>
                Create Ticket
              </Link>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Replies</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(ticket => (
                  <tr key={ticket.id}>
                    <td>#{ticket.id}</td>
                    <td>
                      <Link to={`/tickets/${ticket.id}`} className="text-decoration-none">
                        {ticket.title}
                      </Link>
                    </td>
                    <td>{getCategoryLabel(ticket.category)}</td>
                    <td>{getPriorityBadge(ticket.priority)}</td>
                    <td>{getStatusBadge(ticket.status)}</td>
                    <td>
                      <Badge bg="light" text="dark">
                        {ticket.reply_count || 0}
                      </Badge>
                    </td>
                    <td>{formatDate(ticket.updated_at)}</td>
                    <td>
                      <Link to={`/tickets/${ticket.id}`} className="btn btn-sm btn-outline-primary">
                        <i className="flaticon-view mr-1"></i>
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TicketList;
